import { Group, Button, Title, useMantineTheme, Flex } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useContext, useEffect, useState, memo } from "react";
import NumberInput from "src/components/NumberInput/NumberInput";
import Select, { ISelect } from "src/components/Select";
import {
  CategoryOptionType,
  CreateRecurringChargeInput,
  GetAgreementQuery,
  useCreateRecurringChargeMutation,
  useDeleteRecurringChargeMutation,
  useGetCategoryOptionManyLazyQuery,
  useGetLocationTaxRateNamesQuery,
  useUpdateRecurringChargeMutation,
} from "src/generated/graphql";
import { object, string, number, array, boolean } from "yup";
import { useModals } from "@mantine/modals";
import { AppContext } from "src/api/AppContext";
import FormCard from "src/components/FormCard";
import { GetAgreement } from "src/api/graphql/queries/Agreement";
import TextInput from "src/components/TextInput/TextInput";
import { formatter } from "src/utils/constants";
import MultiSelect from "src/components/MultiSelect";
import Switch from "src/components/Switch/Switch";

type recurringChargeDetail = NonNullable<
  GetAgreementQuery["agreementOne"]
>["recurringCharges"][0];
interface IProps {
  agreementId: string;
  recurringCharge?: recurringChargeDetail;
  handleModal?: (recurringCharge: recurringChargeDetail) => void;
  isModal?: boolean;
  disabled?: boolean;
  isAllowWrite: boolean;
}

const chargeInitialValues = {
  categoryOption: "",
  cost: 0,
  name: "",
  isTaxable: false,
  isPostTax: false,
  taxRates: [],
};

const chargeFormSchema = object().shape({
  categoryOption: string().required("Revenue Category is required"),
  cost: string().required("Cost is required"),
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should must be least 2 letters")
    .max(30, "Name must not be greater than 30 letters")
    .required("Name is required"),
  isTaxable: boolean(),
  isPostTax: boolean(),
  taxRates: array(),
});

type IFormProps = Omit<
  CreateRecurringChargeInput,
  "categoryOption" | "taxRates"
> & {
  categoryOption: string;
  id?: string;
  taxRates: string[];
};

function RecurringChargeDetail({
  agreementId,
  recurringCharge,
  handleModal,
  isModal,
  disabled,
  isAllowWrite,
}: IProps) {
  const modals = useModals();
  const theme = useMantineTheme();
  const [isFormActive] = useDisclosure(!!recurringCharge?.id);
  const refetchQuery = [
    { query: GetAgreement, variables: { id: agreementId } },
  ];
  const [revenueArr, setrevenueArr] = useState<ISelect[]>([]);
  const { currentOrganization, currentLocation } = useContext(AppContext);

  const { data: taxRateNames } = useGetLocationTaxRateNamesQuery({
    variables: { locationId: currentLocation?.id || "" },
    skip: !currentLocation?.id,
  });

  const [getCategories] = useGetCategoryOptionManyLazyQuery({
    onCompleted(data) {
      let _data = data?.categoryOptionMany;
      let _dataRevenue =
        _data
          ?.filter((e) => e.type === CategoryOptionType.Revenue)
          .map((val) => ({
            value: val.id,
            label: val.name,
          })) || [];
      setrevenueArr(_dataRevenue);
    },
  });

  useEffect(() => {
    if (currentOrganization?.id) {
      getCategories({
        variables: { orgId: currentOrganization?.id || "" },
      });
    }
  }, [currentOrganization]);

  useEffect(() => {
    recurringCharge && isModal && resetFormState();
  }, [recurringCharge, isModal]);

  const [updateRecurringCharge, { loading: updateLoading }] =
    useUpdateRecurringChargeMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });

  const [createRecurringCharge, { loading: createLoading }] =
    useCreateRecurringChargeMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });

  const [deleteRecurringCharge, { loading: deleteLoading }] =
    useDeleteRecurringChargeMutation({
      refetchQueries: refetchQuery,
    });

  const resetFormState = () => {
    if (!recurringCharge) return;
    const { categoryOption, name, taxRates, ...rest } = recurringCharge;
    let _obj = {
      ...rest,
      categoryOption: categoryOption.id,
      name: name || "",
      taxRates: taxRates.map((t) => t.id),
    };
    form.setValues(_obj);
    form.resetDirty(_obj);
  };

  const form = useForm<IFormProps>({
    initialValues: chargeInitialValues,
    validate: yupResolver(chargeFormSchema),
  });

  const handleSubmit = async (values: IFormProps) => {
    if (!agreementId) return;
    const { id, taxRates, ...rest } = values;
    let _obj = {
      ...rest,
      ...(!recurringCharge?.id ? { agreement: { id: agreementId } } : {}),
      categoryOption: { id: rest.categoryOption },
      taxRates: taxRates.map((e) => ({ id: e })),
    };
    {
      recurringCharge?.id
        ? updateRecurringCharge({
            variables: {
              data: _obj,
              id: recurringCharge?.id,
            },
          })
        : createRecurringCharge({ variables: { data: _obj } });
    }
  };

  const handleEdit = () => {
    handleModal && handleModal(recurringCharge!);
  };

  const handleDelete = () => {
    recurringCharge?.id &&
      deleteRecurringCharge({
        variables: { deleteRecurringChargeId: recurringCharge?.id },
      });
  };

  const title = `${recurringCharge?.name} \u00A0\u00A0\u00A0 ${
    recurringCharge?.cost && formatter.format(recurringCharge?.cost)
  } \u00A0\u00A0\u00A0 ${recurringCharge?.categoryOption?.name}`;

  return (
    <FormCard
      title={title}
      loading={updateLoading || deleteLoading}
      onEdit={isAllowWrite ? handleEdit : undefined}
      onSave={form.onSubmit((values) => handleSubmit(values))}
      isFormActive={isFormActive}
      hideCard={isModal}
      mb={10}
      size="md"
      radius={4}
      onDelete={isAllowWrite ? handleDelete : undefined}
      disabled={disabled}
    >
      {isModal && (
        <>
          <Flex align="baseline" justify="space-between">
            <Title size={20} weight={400}>
              General
            </Title>
            <Switch
              labelPosition="left"
              label="Taxable"
              checked={form.getInputProps("isTaxable").value}
              {...form.getInputProps("isTaxable")}
            />
          </Flex>

          <TextInput
            mt={"xs"}
            withAsterisk
            label="Name"
            {...form.getInputProps("name")}
          />

          <Flex align={"baseline"} justify={"space-between"}>
            <NumberInput
              withAsterisk
              label="Cost"
              precision={2}
              {...form.getInputProps("cost")}
            />

            <Switch
              labelPosition="left"
              label="Value is Post-Tax"
              checked={form.getInputProps("isPostTax").value}
              {...form.getInputProps("isPostTax")}
            />
          </Flex>

          <MultiSelect
            data={
              taxRateNames?.locationTaxRateMany?.map((val) => ({
                value: val.id,
                label: val.organizationTaxRate?.name,
              })) || []
            }
            label="Tax Rates"
            {...form.getInputProps("taxRates")}
          />

          <Select
            withAsterisk
            data={revenueArr}
            label="Revenue Category"
            {...form.getInputProps("categoryOption")}
          />

          <Group position="apart" mt="md" style={{ marginTop: 30 }}>
            <Button
              radius="md"
              variant="outline"
              onClick={() => modals.closeAll()}
              style={{
                flex: 1,
                color: theme.colors.secondaryTextColor[6],
              }}
              sx={{
                backgroundColor: theme.colors.secondary[6],
                "&:hover": {
                  background: theme.colors.secondary[5],
                },
              }}
            >
              Cancel
            </Button>
            <Button
              radius="md"
              type="submit"
              disabled={createLoading || updateLoading}
              style={{
                flex: 1,
              }}
              sx={{
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
              loading={createLoading || updateLoading}
            >
              {recurringCharge?.id ? "Save" : "Create"}
            </Button>
          </Group>
        </>
      )}
    </FormCard>
  );
}

export default memo(RecurringChargeDetail);
